/**
 * "Where this comes from" — one listener for every explain affordance.
 *
 * The platform's argument is that a number you cannot interrogate is a picture,
 * so almost every figure carries a way to open its source and method. Those
 * affordances are rendered by a dozen different screens, and rather than each
 * wiring its own the demo registers two document-level listeners once.
 *
 * Hand-ported because those registrations are top-level statements — a walker
 * that follows declarations cannot see them, and without them the hero cards
 * say "WHERE THIS COMES FROM →" and do nothing when clicked.
 *
 * Three details are carried over deliberately:
 *
 *   - the precedence. An SMS affordance wins over a button, and a button over a
 *     card, because they nest: a card can contain a button that explains
 *     something narrower than the card does.
 *   - the capture phase. The panes underneath have their own click handlers,
 *     and an explain click must not also select a row.
 *   - Enter as well as click. These are reachable by keyboard, and a control
 *     that can be focused but not activated is worse than one that cannot be
 *     focused at all.
 */
import { withDemo } from "../domain/load";

type Target = { closest?: (s: string) => HTMLElement | null };

function onClick(e: MouseEvent) {
  const t = e.target as unknown as Target;
  if (!t?.closest) return;

  const sms = t.closest("[data-smsx]");
  if (sms) {
    e.preventDefault();
    e.stopPropagation();
    withDemo((d) => d.openSmsExplain(sms.dataset.smsx));
    return;
  }
  const button = t.closest("[data-exb],[data-exb2]");
  if (button) {
    e.preventDefault();
    e.stopPropagation();
    withDemo((d) => d.openExplain(button.dataset.exb || button.dataset.exb2));
    return;
  }
  const card = t.closest("[data-ex],[data-ex2]");
  if (card) {
    e.stopPropagation();
    withDemo((d) => d.openExplain(card.dataset.ex || card.dataset.ex2));
  }
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key !== "Enter") return;
  const t = e.target as unknown as Target;
  const card = t?.closest?.("[data-ex],[data-ex2]");
  if (card) {
    e.preventDefault();
    withDemo((d) => d.openExplain(card.dataset.ex || card.dataset.ex2));
  }
}

/** Wire the affordances for as long as the platform is mounted. */
export function attachExplain(): () => void {
  /* capture, so a pane's own click handler does not also fire */
  window.addEventListener("click", onClick, true);
  window.addEventListener("keydown", onKeyDown);
  return () => {
    window.removeEventListener("click", onClick, true);
    window.removeEventListener("keydown", onKeyDown);
  };
}
